import type { OpportunityType } from '@/types/Opportunity'
import type { ActionType } from '@/types/pursuit/Action'
import type {
  Pursuit,
  PursuitTerminalStatus,
  PursuitType,
} from '@/types/pursuit/Pursuit'
import type { PursuitStage } from '@/types/pursuit/Stage'

// A pursuit is backed by a Sweetspot entity when it tracks a typed opportunity
// (federal contract/grant, SBIR, SLED, DIBBS) or originated from an eBuy email.
export const isPursuitEntityBacked = (pursuit: Pursuit): boolean => {
  if (pursuit.source_opportunity_id) return true
  switch (pursuit.type) {
    case 'dibbs_contract_pursuit':
      return Boolean(pursuit.dibbs_contract_pursuit?.entity_id)
    case 'federal_contract_pursuit':
      return Boolean(pursuit.federal_contract_pursuit?.entity_id)
    case 'federal_grant_pursuit':
      return Boolean(pursuit.federal_grant_pursuit?.entity_id)
    case 'sbir_pursuit':
      return Boolean(pursuit.sbir_pursuit?.entity_id)
    case 'sled_contract_pursuit':
      return Boolean(pursuit.sled_contract_pursuit?.entity_id)
    default: {
      // Tolerate pursuit types this branch doesn't model yet (e.g.
      // intl_contract_pursuit present on dev) instead of crashing — treat an
      // unknown typed pursuit as entity-backed (so it isn't offered GovWin
      // linking). `never` still enforces compile-time exhaustiveness for the
      // types this branch DOES know.
      const exhaustiveCheck: never = pursuit.type
      console.warn(
        `Unhandled pursuit type, treating as entity-backed: ${String(exhaustiveCheck)}`,
      )
      return true
    }
  }
}

// Only a manual/internal pursuit with no provider and no backing Sweetspot
// entity can be linked to a GovWin opportunity.
// Returns a human-readable reason when a pursuit cannot be linked to GovWin,
// or `null` when linking is allowed. Used to explain the disabled state of the
// GovWin "Link" button instead of hiding it entirely.
export const getGovWinLinkDisabledReason = (
  pursuit: Pursuit,
): string | null => {
  if (pursuit.provider === 'govwin') {
    return 'This pursuit is already linked to a GovWin opportunity.'
  }
  if (pursuit.provider === 'salesforce') {
    return 'This pursuit is synced from Salesforce and cannot be linked to GovWin.'
  }
  if (isPursuitEntityBacked(pursuit)) {
    return 'This pursuit is already tracking an opportunity and cannot be linked to GovWin.'
  }
  return null
}

const ActionTypes: Array<ActionType> = [
  'created_comment',
  'created_compliance_item',
  'created_document',
  'created_file',
  'created_reply',
  'created_task',
  'enabled_salesforce_sync',
  'generated_compliance_matrix',
  'updated_pursuit_no_bid',
  'updated_pursuit_status',
  'updated_task_priority',
  'updated_task_status',
]

export const isValidActionType = (type: string): type is ActionType => {
  return ActionTypes.includes(type as ActionType)
}

export const getOpportunityLabelFromPursuitType = (type: PursuitType) => {
  switch (type) {
    case 'dibbs_contract_pursuit':
      return 'DIBBS Contract'
    case 'federal_contract_pursuit':
      return 'Federal Contract'
    case 'federal_grant_pursuit':
      return 'Federal Grant'
    case 'sbir_pursuit':
      return 'SBIR/STTR'
    case 'sled_contract_pursuit':
      return 'SLED Contract'
    default: {
      // Unknown/cross-feature pursuit type (e.g. intl_contract_pursuit on dev) —
      // generic label instead of crashing. `never` keeps known types exhaustive.
      const exhaustiveCheck: never = type
      console.warn(
        `Unhandled pursuit type, using generic label: ${String(exhaustiveCheck)}`,
      )
      return 'Opportunity'
    }
  }
}

export const getPursuitTypeFromOpportunityType = (
  opportunityType: OpportunityType,
): PursuitType => {
  switch (opportunityType) {
    case 'dibbs_contract':
      return 'dibbs_contract_pursuit'
    case 'federal_contract':
      return 'federal_contract_pursuit'
    case 'federal_grant':
      return 'federal_grant_pursuit'
    case 'sbir':
      return 'sbir_pursuit'
    case 'sled_contract':
      return 'sled_contract_pursuit'
    default: {
      // Unknown/cross-feature opportunity type — fall back instead of crashing.
      const exhaustiveCheck: never = opportunityType
      console.warn(
        `Unhandled opportunity type, falling back: ${String(exhaustiveCheck)}`,
      )
      return 'federal_contract_pursuit'
    }
  }
}

export const getOpportunityTypeFromPursuitType = (
  pursuitType: PursuitType,
): OpportunityType => {
  switch (pursuitType) {
    case 'dibbs_contract_pursuit':
      return 'dibbs_contract'
    case 'federal_contract_pursuit':
      return 'federal_contract'
    case 'federal_grant_pursuit':
      return 'federal_grant'
    case 'sbir_pursuit':
      return 'sbir'
    case 'sled_contract_pursuit':
      return 'sled_contract'
    default: {
      // Unknown/cross-feature pursuit type (e.g. intl_contract_pursuit on dev) —
      // fall back instead of crashing the page. `never` keeps known types exhaustive.
      const exhaustiveCheck: never = pursuitType
      console.warn(
        `Unhandled pursuit type, falling back: ${String(exhaustiveCheck)}`,
      )
      return 'federal_contract'
    }
  }
}

export const getMinStageIndex = (pursuitStages: PursuitStage[]) => {
  return Math.min(...pursuitStages.map((stage) => stage.index))
}

export const getMaxStageIndex = (pursuitStages: PursuitStage[]) => {
  return Math.max(...pursuitStages.map((stage) => stage.index))
}

// Won/Lost stages apply their status; any other stage keeps only No Bid.
export const getTerminalStatusForStage = (
  stage: PursuitStage,
  current: PursuitTerminalStatus | null,
): PursuitTerminalStatus | null =>
  stage.status_to_apply ?? (current === 'no_bid' ? 'no_bid' : null)
